export interface SystemSettings {
  // Invoice Settings
  companyName: string;
  companyAddress: string;
  companyPhone: string;
  companyEmail: string;
  companyWebsite: string;
  companyTaxId: string;
  bankName: string;
  bankAccountNumber: string;
  bankIban: string;
  bankSwift: string;
  invoicePrefix: string;
  defaultTaxRate: number;
  paymentTerms: string;
  defaultInvoiceTemplate: string;
  
  // Cart Settings
  handlingFee: number;
  handlingFeeDescription: string;
}

export class SystemSettingsService {
  private static readonly STORAGE_KEY = 'system_settings';
  private static syncPromise: Promise<SystemSettings> | null = null;
  
  private static defaultSettings: SystemSettings = {
    companyName: 'MakerSet',
    companyAddress: '',
    companyPhone: '',
    companyEmail: '',
    companyWebsite: 'https://www.yrgel.com',
    companyTaxId: '',
    bankName: '',
    bankAccountNumber: '',
    bankIban: '',
    bankSwift: '',
    invoicePrefix: 'INV',
    defaultTaxRate: 20, // 20%
    paymentTerms: 'prepayment',
    defaultInvoiceTemplate: 'modern',
    
    // Cart Settings
    handlingFee: 15, // 15€ handling fee
    handlingFeeDescription: 'Handling, Packaging & Transport',
  };

  static getSettings(): SystemSettings {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return { ...this.defaultSettings, ...parsed };
      }
    } catch (error) {
      console.error('Error loading system settings:', error);
    }
    return this.defaultSettings;
  }

  /** Pull invoice/company fields from API into localStorage (source of truth on server). */
  static async syncFromApi(): Promise<SystemSettings> {
    if (this.syncPromise) return this.syncPromise;
    this.syncPromise = (async () => {
      try {
        const { api } = await import('./api');
        const res = await api.get('/system-settings');
        const data = (res.data as any)?.settings || {};
        const mapped: Partial<SystemSettings> = {
          companyName: data.company_name ?? data.companyName,
          companyAddress: data.company_address ?? data.companyAddress,
          companyPhone: data.company_phone ?? data.companyPhone,
          companyEmail: data.company_email ?? data.companyEmail,
          companyWebsite: data.company_website ?? data.companyWebsite,
          companyTaxId: data.company_tax_id ?? data.companyTaxId,
          bankName: data.bank_name ?? data.bankName,
          bankAccountNumber: data.bank_account_number ?? data.bankAccountNumber,
          bankIban: data.bank_iban ?? data.bankIban,
          bankSwift: data.bank_swift ?? data.bankSwift,
          invoicePrefix: data.invoice_prefix ?? data.invoicePrefix,
          paymentTerms: data.payment_terms ?? data.paymentTerms,
          defaultInvoiceTemplate:
            data.default_invoice_template ?? data.defaultInvoiceTemplate,
        };
        if (data.default_tax_rate != null || data.defaultTaxRate != null) {
          mapped.defaultTaxRate = Number(data.default_tax_rate ?? data.defaultTaxRate);
        }
        if (data.tax_rate != null && mapped.defaultTaxRate == null) {
          mapped.defaultTaxRate = Number(data.tax_rate);
        }
        // Drop undefined so we don't wipe good local values with undefined
        Object.keys(mapped).forEach((k) => {
          if ((mapped as any)[k] == null || (mapped as any)[k] === '') {
            delete (mapped as any)[k];
          }
        });
        if (Object.keys(mapped).length > 0) {
          this.saveSettings(mapped);
        }
      } catch (error) {
        console.warn('SystemSettingsService.syncFromApi failed:', error);
      } finally {
        this.syncPromise = null;
      }
      return this.getSettings();
    })();
    return this.syncPromise;
  }

  /** True when company looks like the old hardcoded invoice placeholder. */
  static isPlaceholderCompany(company?: { name?: string; address?: string } | null): boolean {
    if (!company?.name) return true;
    const name = String(company.name).trim().toLowerCase();
    const address = String(company.address || '').toLowerCase();
    return (
      name === 'makerset solutions' ||
      name === 'makerset' ||
      address.includes('tech city, tc 12345') ||
      address.includes('123 innovation street')
    );
  }

  static saveSettings(settings: Partial<SystemSettings>): void {
    try {
      const currentSettings = this.getSettings();
      const updatedSettings = { ...currentSettings, ...settings };
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(updatedSettings));
    } catch (error) {
      console.error('Error saving system settings:', error);
    }
  }

  static resetSettings(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      console.error('Error resetting system settings:', error);
    }
  }

  static getCompanyInfo() {
    const settings = this.getSettings();
    return {
      name: settings.companyName,
      address: settings.companyAddress,
      phone: settings.companyPhone,
      email: settings.companyEmail,
      website: settings.companyWebsite,
      taxId: settings.companyTaxId,
      bankAccount: {
        bankName: settings.bankName,
        accountNumber: settings.bankAccountNumber,
        iban: settings.bankIban,
        swift: settings.bankSwift,
      }
    };
  }

  static getInvoiceSettings() {
    const settings = this.getSettings();
    return {
      prefix: settings.invoicePrefix,
      taxRate: settings.defaultTaxRate / 100, // Convert percentage to decimal
      paymentTerms: settings.paymentTerms,
      template: settings.defaultInvoiceTemplate,
    };
  }
}
